import { useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import mapboxgl from 'mapbox-gl';
import { useMapStore } from 'src/stores';
import { getCurrentPosition } from 'src/utils';

import UserMarker from './user-marker';

export function UserMarkersLayer({
  travellers,
  showTravellers = true,
  showFriendsOnly = false,
  user,
  handleUserClick,
  mapLoaded
}) {
  const { mapInstance, registerCleanup, unregisterCleanup } = useMapStore();
  const userMarkersRef = useRef([]);

  const cleanupUserMarkers = () => {
    userMarkersRef.current.forEach((marker) => marker.remove());
    userMarkersRef.current = [];
  };

  const isValidCoords = (coords) =>
    Array.isArray(coords) &&
    coords.length === 2 &&
    Number.isFinite(coords[0]) &&
    Number.isFinite(coords[1]);

  useEffect(() => {
    registerCleanup(cleanupUserMarkers);
    return () => unregisterCleanup(cleanupUserMarkers);
  }, [registerCleanup, unregisterCleanup]);

  useEffect(() => {
    if (!mapInstance || !mapLoaded || travellers.length === 0) return;

    cleanupUserMarkers();
    if (!showTravellers) return;

    let filteredUsers = travellers;
    if (showFriendsOnly && user?.friends?.length > 0) {
      filteredUsers = travellers.filter((u) => user.friends.includes(u.id));
    }

    filteredUsers.forEach((u) => {
      if (!isValidCoords(u.locationCoords)) return;

      const el = document.createElement('div');
      const root = createRoot(el);

      const isCurrentUser = user && u.id === user.id;
      if (isCurrentUser) return;

      root.render(
        <UserMarker u={u} isCurrentUser={isCurrentUser} />
      );

      const marker = new mapboxgl.Marker(el, {
        anchor: 'center',
        offset: [0, 0],
      })
        .setLngLat(u.locationCoords)
        .addTo(mapInstance);

      userMarkersRef.current.push(marker);

      el.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        handleUserClick(u.id);
      });
    });
  }, [mapInstance, mapLoaded, travellers, showTravellers, showFriendsOnly, user]);

  useEffect(() => {
    if (!mapInstance || !mapLoaded || !user) return;

    const currentMarker = userMarkersRef.current.find((m) => {
      const lngLat = m.getLngLat();
      return lngLat.lng === user.locationCoords?.[0] && lngLat.lat === user.locationCoords?.[1];
    });

    // Hidden -> remove marker if it exists
    if (user.privacy === "Hidden") {
      if (currentMarker) {
        currentMarker.remove();
        userMarkersRef.current = userMarkersRef.current.filter(m => m !== currentMarker);
      }
      return;
    }

    // Pinned -> update coords
    if (user.privacy === "Pinned") {
      const coords = user.pinnedLocation?.coordinates;

      console.log(coords);
      if (!coords || !Array.isArray(coords) || coords.length !== 2) {
        console.log("Invalid pinned coordinates for user:", user);
        // No coords yet → do nothing (wait for update)
        if (currentMarker) {
          currentMarker.remove();
          userMarkersRef.current = userMarkersRef.current.filter(m => m !== currentMarker);
        }
        return;
      }

      // valid coords
      if (currentMarker) {
        currentMarker.setLngLat(coords);
      } else {
        const el = document.createElement("div");
        const root = createRoot(el);
        root.render(<UserMarker u={user} isCurrentUser />);
        const marker = new mapboxgl.Marker(el).setLngLat(coords).addTo(mapInstance);
        userMarkersRef.current.push(marker);
      }

      mapInstance.flyTo({ center: coords, zoom: 6, speed: 1.2, curve: 1.42 });
    }

    // Live -> fetch coords and update
    if (user.privacy === "Live") {
      (async () => {
        try {
          const { latitude, longitude } = await getCurrentPosition(); 
          const coords = [longitude, latitude];
          if (currentMarker) {
            currentMarker.setLngLat(coords);
          } else {
            const el = document.createElement('div');
            const root = createRoot(el);
            root.render(<UserMarker u={user} isCurrentUser />);
            const marker = new mapboxgl.Marker(el).setLngLat(coords).addTo(mapInstance);
            userMarkersRef.current.push(marker);
          }
          mapInstance.flyTo({ center: coords, zoom: 6, speed: 1.2, curve: 1.42 });
        } catch (err) {
          console.error("Failed to fetch live coords:", err);
        }
      })();
    }
  }, [user?.privacy, user?.pinnedLocation?.coordinates, mapInstance, mapLoaded]);

  useEffect(() => {
    if (!user) {
      cleanupUserMarkers();
    }
  }, [user]);

  useEffect(() => {
    return () => {
      cleanupUserMarkers();
    };
  }, []);

  return null;
}
